import { UiState } from 'instantsearch.js';
import { history } from 'instantsearch.js/es/lib/routers';
import { RouterProps } from 'instantsearch.js/es/middlewares';
import {
  engineSizeFormatter,
  makeModelFormatter,
  paymentTypeFormatter,
  priceFormatter,
  registeredAtFormatter,
  searchTypeFormatter,
  singleSelectNumericFormatter,
  sortOrderFormatter,
} from './formatters';
import {
  booleanParser,
  registeredAtParser,
  makeModelParser,
  searchTypeParser,
  singleSelectNumericParser,
  sortOrderParser,
} from './parsers';
import type { InstantSearchInitialState } from '../instantSearchProvider';

type RouteState = {
  age: string;
  body_type: string[];
  branch_id: string;
  capcode: string;
  colour: string[];
  distance: string;
  doors: string[];
  dor: string;
  drive_train: string[];
  features: string[];
  franchise: string;
  fuel_type: string[];
  insurance_group_range: string[];
  keywords: string;
  location: string;
  make: string;
  make_model: { [x: string]: string[] };
  max_engine_size: string;
  max_price: string;
  mileage: string;
  min_engine_size: string;
  min_price: string;
  model: string;
  mpg: string;
  payment_type: 'cash' | 'monthly';
  promotion: string;
  query: string;
  roadtax_cost: string;
  search_type: string;
  seats: string[];
  show_click_and_collect_options: string;
  sort_order: string;
  transmission: string;
  trim: string;
  unreserved_only: string;
};

//changes to data arising from parseURL:
interface ParsedRouteState
  extends Omit<
    RouteState,
    | 'make'
    | 'model'
    | 'make_model'
    | 'promotion'
    | 'show_click_and_collect_options'
    | 'trim'
    | 'unreserved_only'
  > {
  cashPrice: string;
  engineSize: string;
  make_model: Record<string, string[]>;
  monthlyPayment: string;
  promotion: boolean;
  show_click_and_collect_options: boolean;
  unreserved_only: boolean;
}

// all the refinement widgets from instantSearch, plus the relatedRefinementList we made
type ACUiState = {
  [indexName: string]: UiState[string] & {
    relatedRefinementList: Record<string, string[]>;
  };
};

//indexName is variable by env, so we'll need to pass it in as an argument
const routing = (
  indexName: string,
  initialState: InstantSearchInitialState | null
): RouterProps<ACUiState, Partial<ParsedRouteState>> => ({
  router: history({
    //removes the index name from the url
    //allows for the format/encoding of the url to be tweaked
    createURL({ qsModule, routeState }) {
      const queryParams = qsModule
        .stringify(routeState, { arrayFormat: 'brackets' })
        .replace(/%2B/g, '+')
        .replace(/%20/g, '+');

      const url = `${location.href.split('?')[0]}${
        queryParams !== '' ? '?' + queryParams : ''
      }`;

      if (initialState) {
        //if we have an initial state, we need to remove the /make/model from the URL
        return url.replace(`/${initialState.make}/${initialState.model}`, '');
      }

      // store last search URL in localStorage
      localStorage.setItem('lastSearch', `/vehicles?${queryParams}`);

      //this is what gets displayed in the URL bar
      return url;
    },
    //parses the url and returns the state object for the UI
    parseURL({ qsModule, location }) {
      //grab everything in the url after ? and convert to an object
      const urlState = qsModule.parse(
        location.search.slice(1)
      ) as Partial<RouteState>;

      const {
        age,
        branch_id,
        dor,
        make,
        make_model,
        max_engine_size,
        max_price,
        mileage,
        min_engine_size,
        min_price,
        model,
        payment_type,
        promotion,
        roadtax_cost,
        search_type,
        show_click_and_collect_options,
        sort_order,
        trim,
        unreserved_only,
      } = urlState;

      const handleMakeModel = () => {
        return initialState
          ? makeModelParser(initialState)
          : makeModelParser(make ? { make, model, trim } : make_model);
      };

      // if values need parsed for instantSearch widgets state, do it here
      const parsed: Partial<ParsedRouteState> = {
        ...urlState,
        age: singleSelectNumericParser(age),
        branch_id: branch_id?.toUpperCase(),
        make_model: handleMakeModel(),
        mileage: singleSelectNumericParser(mileage),
        mpg: singleSelectNumericParser(urlState.mpg, 'over'),
        promotion: booleanParser(promotion),
        roadtax_cost: singleSelectNumericParser(roadtax_cost),
        search_type: searchTypeParser(search_type),
        show_click_and_collect_options: booleanParser(
          show_click_and_collect_options
        ),
        sort_order: sortOrderParser(sort_order, indexName),
        unreserved_only: booleanParser(unreserved_only),
      };

      if (min_engine_size || max_engine_size) {
        parsed.engineSize = `${min_engine_size}:${max_engine_size}`;
      }

      if (dor) {
        parsed.dor = registeredAtParser(dor);
      }

      if (payment_type) {
        const priceRange = `${min_price}:${max_price}`;
        parsed[payment_type === 'cash' ? 'cashPrice' : 'monthlyPayment'] =
          priceRange;
      }

      return parsed;
    },
  }),
  stateMapping: {
    stateToRoute(uiState) {
      const indexUiState = uiState[indexName];

      const {
        menu,
        numericMenu,
        query,
        range,
        refinementList,
        relatedRefinementList,
        sortBy,
        toggle,
      } = indexUiState;

      const {
        branchID,
        capCode,
        conditions,
        highlightedFeature,
        transmissionType,
        ['branchInfo.franchiseCodes']: franchise,
      } = menu || {};

      const { age, fuelConsumption, mileage, roadtax } = numericMenu || {};

      const { cashPrice, monthlyPayment, engineSize, registeredAt } =
        range || {};

      const {
        bodyType,
        bucketedFeatures,
        colour,
        doors,
        driveTrain,
        fuelType,
        insuranceGroupRange,
        seats,
      } = refinementList || {};

      const { clickAndCollectAvailable, isReserved, promotion } = toggle || {};

      const params = new URLSearchParams(window.location.search);

      // if state needs formatted for the URL, do it here
      const routeState: Partial<ParsedRouteState> = {
        age: singleSelectNumericFormatter(age),
        body_type: bodyType,
        branch_id: branchID?.toLowerCase(),
        capcode: capCode,
        colour,
        distance: params.get('distance') || undefined,
        doors,
        dor: registeredAtFormatter(registeredAt),
        drive_train: driveTrain,
        features: bucketedFeatures,
        franchise,
        fuel_type: fuelType,
        insurance_group_range: insuranceGroupRange,
        keywords: highlightedFeature,
        location: params.get('location')?.replace(/\s/g, '+') || undefined,
        make_model: makeModelFormatter(relatedRefinementList),
        mileage: singleSelectNumericFormatter(mileage),
        max_engine_size: engineSizeFormatter(engineSize, 'max'),
        mpg: singleSelectNumericFormatter(fuelConsumption),
        min_engine_size: engineSizeFormatter(engineSize, 'min'),
        min_price: priceFormatter(cashPrice || monthlyPayment, 'min'),
        max_price: priceFormatter(cashPrice || monthlyPayment, 'max'),
        payment_type: paymentTypeFormatter(indexUiState),
        promotion: promotion || undefined,
        query,
        search_type: searchTypeFormatter(conditions),
        roadtax_cost: singleSelectNumericFormatter(roadtax),
        seats,
        show_click_and_collect_options: clickAndCollectAvailable || undefined,
        sort_order: sortOrderFormatter(sortBy, indexName),
        transmission: transmissionType,
        unreserved_only: isReserved || undefined,
      };

      return routeState;
    },
    routeToState(routeState) {
      const {
        age,
        body_type,
        branch_id,
        capcode,
        cashPrice,
        colour,
        doors,
        dor,
        drive_train,
        engineSize,
        features,
        franchise,
        fuel_type,
        insurance_group_range,
        keywords,
        make_model,
        mileage,
        monthlyPayment,
        mpg,
        promotion,
        query,
        roadtax_cost,
        search_type,
        seats,
        show_click_and_collect_options,
        sort_order,
        transmission,
        unreserved_only,
      } = routeState;

      // here's where we resolve any mismatch between classic URL params/instantSearch properties
      // eg. search_type -> conditions, mpg -> fuelConsumption, etc.
      return {
        [indexName]: {
          menu: {
            ['branchInfo.franchiseCodes']: franchise,
            branchID: branch_id,
            capCode: capcode,
            conditions: search_type,
            highlightedFeature: keywords,
            transmissionType: transmission,
          },
          numericMenu: {
            age,
            fuelConsumption: mpg,
            mileage,
            roadtax: roadtax_cost,
          },
          query,
          range: {
            cashPrice,
            engineSize,
            monthlyPayment,
            registeredAt: dor,
          },
          refinementList: {
            bodyType: body_type,
            bucketedFeatures: features,
            colour,
            doors,
            driveTrain: drive_train,
            fuelType: fuel_type,
            insuranceGroupRange: insurance_group_range,
            seats,
          },
          relatedRefinementList: make_model,
          sortBy: sort_order,
          toggle: {
            clickAndCollectAvailable: show_click_and_collect_options,
            isReserved: unreserved_only,
            promotion,
          },
        },
      } as unknown as ACUiState;
    },
  },
});

export default routing;
